import React, { useEffect, useRef, useState } from "react";
import {
    HeartHandshake,
    RefreshCcw,
    X,
    ChevronDown,
    AlertCircle,
} from "lucide-react";
import { useData } from "../contexts/DataContext";
import { useClara } from "../contexts/ClaraContext";

/**
 * DemoControls — small popover anchored on the Demo badge.
 * Provides:
 *  - Simulate inactivity check-in (triggers Clara wellness prompt)
 *  - Reset demo data (clears localStorage and reloads state)
 */
export const DemoControls = ({ onSimulateInactivity }) => {
    const { resetDemo } = useData();
    const { speak } = useClara();
    const [open, setOpen] = useState(false);
    const popoverRef = useRef(null);
    const buttonRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const onDocClick = (e) => {
            if (
                popoverRef.current &&
                !popoverRef.current.contains(e.target) &&
                buttonRef.current &&
                !buttonRef.current.contains(e.target)
            ) {
                setOpen(false);
            }
        };
        const onKey = (e) => {
            if (e.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", onDocClick);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDocClick);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const handleSimulate = () => {
        setOpen(false);
        onSimulateInactivity?.();
    };

    const handleReset = () => {
        setOpen(false);
        resetDemo();
        speak("Demo data reset.");
    };

    return (
        <div className="relative">
            <button
                ref={buttonRef}
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-label="Demo controls"
                aria-expanded={open}
                aria-haspopup="dialog"
                data-testid="demo-mode-badge"
                className="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-wider uppercase text-[#B8860B] bg-[#F0EAD6] border border-[#B8860B]/30 hover:bg-[#E8DDB5] rounded-full px-3 py-1.5 min-h-[28px] transition-colors"
            >
                <AlertCircle className="w-3.5 h-3.5" aria-hidden="true" />
                Demo
                <ChevronDown
                    className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`}
                    aria-hidden="true"
                />
            </button>

            {open && (
                <div
                    ref={popoverRef}
                    role="dialog"
                    aria-label="Demo controls"
                    data-testid="demo-controls-popover"
                    className="absolute right-0 top-[calc(100%+8px)] z-40 w-72 bg-white rounded-2xl border border-[#0A3D7A]/15 shadow-2xl p-3 fade-in-up"
                >
                    <div className="flex items-center justify-between px-1 pb-2">
                        <p className="font-display font-bold text-[#1A1A2E]">
                            Demo controls
                        </p>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            aria-label="Close demo controls"
                            className="w-9 h-9 rounded-lg hover:bg-[#FFF8F0] flex items-center justify-center text-[#5A5A6E]"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                    <p className="text-xs text-[#5A5A6E] px-1 pb-3">
                        This is a prototype. No real data is collected,
                        transmitted, or stored remotely.
                    </p>
                    <div className="space-y-2">
                        <button
                            type="button"
                            onClick={handleSimulate}
                            data-testid="demo-simulate-inactivity-btn"
                            className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#FFF8F0] border border-[#F0EAD6] hover:border-[#0A3D7A] transition-colors text-left"
                        >
                            <span
                                aria-hidden="true"
                                className="shrink-0 w-9 h-9 rounded-lg bg-[#B8860B] flex items-center justify-center text-[#1A1A2E]"
                            >
                                <HeartHandshake className="w-4 h-4" />
                            </span>
                            <span className="flex-1 min-w-0">
                                <span className="block font-semibold text-[#1A1A2E]">
                                    Simulate inactivity check-in
                                </span>
                                <span className="block text-xs text-[#5A5A6E]">
                                    Clara asks "Are you okay, Maria?"
                                </span>
                            </span>
                        </button>
                        <button
                            type="button"
                            onClick={handleReset}
                            data-testid="demo-reset-btn"
                            className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#FFF8F0] border border-[#F0EAD6] hover:border-[#A31621] transition-colors text-left"
                        >
                            <span
                                aria-hidden="true"
                                className="shrink-0 w-9 h-9 rounded-lg bg-[#A31621] flex items-center justify-center text-white"
                            >
                                <RefreshCcw className="w-4 h-4" />
                            </span>
                            <span className="flex-1 min-w-0">
                                <span className="block font-semibold text-[#1A1A2E]">
                                    Reset demo data
                                </span>
                                <span className="block text-xs text-[#5A5A6E]">
                                    Restore default contacts and history
                                </span>
                            </span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DemoControls;
